import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { CENTERED_PAGE_CLASS } from '../components/pageLayout';
import { useTranslation, Trans } from 'react-i18next';



import { MCPClient, OrderItem, ServiceType } from '@the-real-insight/tri-model';
import { PaymentMode } from '@the-real-insight/tri-model';
import { InvoicingPeriod } from '@the-real-insight/tri-model';
import { useCustomerTheme } from '../Theme';
import { useModalContext } from '../components/ModalProvider';
import { useAtom } from 'jotai';
import { authenticationAtom } from '../State';
import { userAPI } from '../UserAPI';
import { useSearchParams } from 'react-router-dom';
import RAGManagement from '../real-gain/rag/RAGManagement';
import AgentToolsetManagement from '../real-gain/AgentToolsetManagement';
import PromptToolManagement from '../real-gain/prompt-tool/PromptToolManagement';
import AgenticWorkflowManagement from '../real-gain/agentic-workflow/AgenticWorkflowManagement';
import { useTestDialog } from '../real-gain/hooks/useTestDialog';
import { usePublishDialog } from '../real-gain/hooks/usePublishDialog';
import dayjs from 'dayjs';
import ApiKeyManagement from './ApiKeyManagement';
import MarketplaceStore from './MarketplaceStore';
import MarketplaceOfferPage from './MarketplaceOfferPage';
import { FILTER_PARAMS, hasCollection, parseCategory, parsePrice, parseScope, storeHref } from './marketplaceFilters';
import MarketplaceNav, { type MarketplaceCrumb } from './MarketplaceNav';
import { scrollToTop } from './scrollToTop';
import { fetchMarketplaceData, getCachedMarketplaceData } from './marketplaceData';
import { useRoleAccess } from '../hooks/useRoleAccess';

import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent } from '@/components/ui/tabs';
// Helper function to check if an offer is from another provider (not own company)
const isExternalOffer = (offer: any, customerTenant: string): boolean => {
    return offer._tenant !== customerTenant;
};

// Helper function to calculate total plan price (baseFee * term) for comparison
const calculateTotalPlanPrice = (plan: any): number => {
    const baseFee = typeof plan.baseFee === 'number' ? plan.baseFee : 0;
    const term = typeof plan.term === 'number' ? plan.term : 1;
    return baseFee * term;
};

// Helper function to get billing algorithm from a plan
const getPlanBillingAlgorithm = (plan: any): string | null => {
    if (plan.billings && plan.billings.length > 0) {
        return plan.billings[0].algorithm || null;
    }
    return null;
};

// Interface for existing order plan info
interface ExistingPlanInfo {
    planName: string;
    baseFee: number;
    term: number;
    totalPrice: number;
    billingAlgorithm: string | null;
}

// Plan status type
type PlanStatus = 'available' | 'current' | 'upgrade' | 'downgrade' | 'incompatible';

// Billing address structure
interface BillingAddress {
    company: string;
    street: string;
    streetNumber: string;
    postalCode: string;
    city: string;
    country: string;
    taxId?: string;
    tradeRegisterId?: string;
    tradeRegisterCity?: string;
}

interface Properties {
}


export default function AgentPanel(_properties: Properties) {
    const { t } = useTranslation();
    const { theme: _theme, setTheme: _setTheme } = useCustomerTheme();
    const [authentication] = useAtom(authenticationAtom) as any;
    const [searchParams, setSearchParams] = useSearchParams();
    const { pushError, showMessageDialog } = useModalContext() as any;
    const fieldId = useId();
    const [_publishDialogOpen, _setPublishDialogOpen] = useState(false);
    const [orderDialogOpen, setOrderDialogOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState<string | undefined>(undefined);
    const [agreedToTerms, setAgreedToTerms] = useState(false);

    // Existing order state for upgrade/downgrade logic
    const [existingProductPlans, setExistingProductPlans] = useState<ExistingPlanInfo[]>([]);
    const [loadingOrders, setLoadingOrders] = useState(false);

    // Check if multiple plans are booked (weird case - disable all ordering)
    const hasMultipleBookedPlans = existingProductPlans.length > 1;

    const roleAccess = useRoleAccess();
    const hasApiKeyAccess = roleAccess.canSeeApiKeys;

    // Billing address state
    const [hasLegalPerson, setHasLegalPerson] = useState(true);
    const [loadingBillingInfo, setLoadingBillingInfo] = useState(false);
    const [billingAddress, setBillingAddress] = useState<BillingAddress>({
        company: '',
        street: '',
        streetNumber: '',
        postalCode: '',
        city: '',
        country: 'DE',
        taxId: '',
        tradeRegisterId: '',
        tradeRegisterCity: '',
    });
    const [billingErrors, setBillingErrors] = useState<Record<string, string>>({});
    const [showBusinessDetails, setShowBusinessDetails] = useState(false);
    const [placingOrder, setPlacingOrder] = useState(false);

    const tab = searchParams.get('tab') || 'marketplace';
    const offerId = searchParams.get('offer');
    const viewTopRef = useRef<HTMLDivElement>(null);
    // One key per page of the marketplace: a change of key plays the page transition.
    const viewKey = offerId
        ? `offer:${offerId}`
        : hasCollection(searchParams)
            ? `collection:${['provider', 'category', 'scope', 'price'].map((key) => searchParams.get(key) ?? '').join('|')}`
            : 'store';
    // Coming back to the marketplace starts from the last result (refreshed in the background).
    const [marketplaceOffers, setMarketplaceOffers] = useState<any[]>(() => getCachedMarketplaceData()?.offers ?? []);
    const [loadingMarketplaceOffers, setLoadingMarketplaceOffers] = useState<boolean>(() => !getCachedMarketplaceData());
    const [topMarketplaceOffers, setTopMarketplaceOffers] = useState<any[]>(() => getCachedMarketplaceData()?.featured ?? []);
    const [selectedMarketplaceOffer, setSelectedMarketplaceOffer] = useState<any>(null);

    const termsTextId = `${fieldId}-terms`;
    const billingFieldId = (field: string) => `${fieldId}-billing-${field}`;

    const { TestDialog, openTestDialog } = useTestDialog({
        onOrder: roleAccess.canPlaceOrders ? (marketplaceOffer) => {
            setSelectedMarketplaceOffer(marketplaceOffer);
            setOrderDialogOpen(true);
        } : undefined,
        canOrder: (marketplaceOffer) => isExternalOffer(marketplaceOffer, authentication.customer),
    });

    const { PublishDialog, openPublishDialog: _openPublishDialog } = usePublishDialog({
        onPublish: async () => {
            // Add your publish logic here
        }
    });

    const openOrderDialog = async (marketPlaceOffer: any) => {
        console.log('======> openOrderDialog', marketPlaceOffer);

        if (!roleAccess.canPlaceOrders) {
            return;
        }

        if (marketPlaceOffer.plans.length === 0) {
                            showMessageDialog(t('agentPanel.noPricingModelsAvailable'), t('agentPanel.noPricingModelsDefined'));
            return;
        }

        // Own offers have no order button anywhere; this only guards a stale deep link.
        if (!isExternalOffer(marketPlaceOffer, authentication.customer)) {
            return;
        }

        setSelectedMarketplaceOffer(marketPlaceOffer);
        setSelectedPlan(marketPlaceOffer.plans[0].name);
        setAgreedToTerms(false);
        setExistingProductPlans([]);
        setBillingErrors({});
        setShowBusinessDetails(false);
        setOrderDialogOpen(true);

        // Fetch billing info to check if we need to collect billing address
        setLoadingBillingInfo(true);
        try {
            const { hasLegalPerson: hasLP, billingInfo, customerName } = await userAPI.getCustomerBillingInfo(authentication.customer);
            console.log('Fetched billing info:', { hasLP, billingInfo, customerName });
            setHasLegalPerson(hasLP);

            if (hasLP && billingInfo) {
                setBillingAddress({
                    company: billingInfo.company || customerName || '',
                    street: billingInfo.street || '',
                    streetNumber: billingInfo.streetNumber || '',
                    postalCode: billingInfo.postalCode || '',
                    city: billingInfo.city || '',
                    country: billingInfo.country || 'DE',
                    taxId: billingInfo.taxId || '',
                    tradeRegisterId: billingInfo.tradeRegisterId || '',
                    tradeRegisterCity: billingInfo.tradeRegisterCity || '',
                });
            } else {
                setBillingAddress(prev => ({
                    ...prev,
                    company: customerName || ''
                }));
            }
        } catch (err) {
            console.error('Error fetching billing info:', err);
            setHasLegalPerson(false);
        } finally {
            setLoadingBillingInfo(false);
        }

        // Fetch existing orders for this product
        setLoadingOrders(true);
        try {
            const orders = await userAPI.getConsumerOrders(authentication.customer);
            console.log('Fetched consumer orders:', orders);

            const bookedPlans: ExistingPlanInfo[] = [];

            // Find ALL orders that contain this marketplace offer
            for (const order of orders) {
                if (order.orderItems && Array.isArray(order.orderItems)) {
                    for (const item of order.orderItems) {
                        if (item.serviceReference === marketPlaceOffer._id && item.plan) {
                            console.log('Found existing order for this product:', item);

                            const orderPlanBaseFee = item.plan.baseFee || 0;
                            const orderPlanTerm = item.plan.term || 1;

                            // Check if this plan is already in our list (avoid duplicates)
                            const alreadyExists = bookedPlans.some(p =>
                                p.baseFee === orderPlanBaseFee && p.term === orderPlanTerm
                            );

                            if (!alreadyExists) {
                                // Try to get plan name
                                let planName = item.plan.name || 'Unbekannter Plan';

                                // If name not found, try marketplace offer plans
                                if (!item.plan.name && marketPlaceOffer.plans) {
                                    const matchingPlan = marketPlaceOffer.plans.find((p: any) =>
                                        p.baseFee === orderPlanBaseFee && p.term === orderPlanTerm
                                    );
                                    if (matchingPlan?.name) {
                                        planName = matchingPlan.name;
                                    }
                                }

                                bookedPlans.push({
                                    planName,
                                    baseFee: orderPlanBaseFee,
                                    term: orderPlanTerm,
                                    totalPrice: calculateTotalPlanPrice(item.plan),
                                    billingAlgorithm: getPlanBillingAlgorithm(item.plan),
                                });
                            }
                        }
                    }
                }
            }

            console.log('Found booked plans:', bookedPlans);
            setExistingProductPlans(bookedPlans);

            // If there are existing plans, pre-select the first available upgrade plan
            if (bookedPlans.length === 1) {
                const upgradePlan = marketPlaceOffer.plans.find((plan: any) => {
                    const planTotalPrice = calculateTotalPlanPrice(plan);
                    const planAlgorithm = getPlanBillingAlgorithm(plan);
                    const existingPlan = bookedPlans[0];

                    const isCompatible = !existingPlan.billingAlgorithm ||
                        !planAlgorithm ||
                        planAlgorithm === existingPlan.billingAlgorithm;

                    return isCompatible && planTotalPrice > existingPlan.totalPrice;
                });

                if (upgradePlan) {
                    setSelectedPlan(upgradePlan.name);
                }
            }
        } catch (err) {
            console.error('Error fetching orders:', err);
            setExistingProductPlans([]);
        } finally {
            setLoadingOrders(false);
        }
    }

    // Helper function to check if a plan is booked
    const isPlanBooked = (plan: any): boolean => {
        return existingProductPlans.some(ep =>
            ep.baseFee === plan.baseFee && ep.term === plan.term
        );
    };

    // Helper function to get plan status
    const getPlanStatus = (plan: any): PlanStatus => {
        // If this plan is booked, it's current
        if (isPlanBooked(plan)) return 'current';

        // If no plans are booked, all are available
        if (existingProductPlans.length === 0) return 'available';

        // If multiple plans are booked, don't allow any changes
        if (hasMultipleBookedPlans) return 'incompatible';

        // Single plan booked - check for upgrade/downgrade/compatibility
        const existingPlan = existingProductPlans[0];
        const planTotalPrice = calculateTotalPlanPrice(plan);
        const planAlgorithm = getPlanBillingAlgorithm(plan);

        // Check compatibility
        const isCompatible = !existingPlan.billingAlgorithm ||
            !planAlgorithm ||
            planAlgorithm === existingPlan.billingAlgorithm;
        if (!isCompatible) return 'incompatible';

        // Check if upgrade or downgrade
        if (planTotalPrice > existingPlan.totalPrice) return 'upgrade';
        if (planTotalPrice < existingPlan.totalPrice) return 'downgrade';

        return 'available';
    };

    // Helper function to get badge for plan status
    const getPlanBadge = (status: PlanStatus) => {
        switch (status) {
            case 'current':
                return <Badge variant="success">{t('agentPanel.planBooked')}</Badge>;
            case 'downgrade':
                return <Badge variant="secondary">{t('agentPanel.planDowngrade')}</Badge>;
            case 'incompatible':
                return <Badge variant="warning">{t('agentPanel.planIncompatible')}</Badge>;
            case 'upgrade':
                return <Badge>{t('agentPanel.planUpgrade')}</Badge>;
            default:
                return null;
        }
    };

    // Check if any upgrade is available
    const hasUpgradeAvailable = selectedMarketplaceOffer?.plans?.some((plan: any) => getPlanStatus(plan) === 'upgrade');

    // Validate billing address
    const validateBillingAddress = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!billingAddress.company.trim()) {
            newErrors.company = t('agentPanel.billingCompanyRequired');
        }
        if (!billingAddress.street.trim()) {
            newErrors.street = t('agentPanel.billingStreetRequired');
        }
        if (!billingAddress.streetNumber.trim()) {
            newErrors.streetNumber = t('agentPanel.billingStreetNumberRequired');
        }
        if (!billingAddress.postalCode.trim()) {
            newErrors.postalCode = t('agentPanel.billingPostalCodeRequired');
        }
        if (!billingAddress.city.trim()) {
            newErrors.city = t('agentPanel.billlingCityRequired');
        }

        setBillingErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /**
     * Pflichtfeld der Rechnungsadresse — Label, Eingabe und Fehlermeldung verbunden.
     *
     * Bewusst eine Render-Funktion und *keine* Komponente: als Komponente wäre
     * ihr Typ bei jedem Tastendruck neu und React würde das Eingabefeld
     * ausbauen und neu anlegen — der Fokus ginge nach jedem Zeichen verloren.
     */
    const renderBillingField = ({
        field,
        label,
        placeholder,
        required,
        className,
    }: {
        field: keyof BillingAddress;
        label: string;
        placeholder?: string;
        required?: boolean;
        className?: string;
    }) => {
        const id = billingFieldId(field);
        const error = billingErrors[field];

        return (
            <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
                <Label htmlFor={id}>
                    {label}
                    {required && <span aria-hidden className="ml-0.5 text-destructive">*</span>}
                </Label>
                <Input
                    id={id}
                    value={billingAddress[field] || ''}
                    placeholder={placeholder}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? `${id}-error` : undefined}
                    onChange={(event) => setBillingAddress({ ...billingAddress, [field]: event.target.value })}
                />
                {error && <p id={`${id}-error`} role="alert" className="text-sm text-destructive">{error}</p>}
            </div>
        );
    };

    const _deleteServer = async (server: any) => {
        const _mcpClient = new MCPClient(server.serverURL);

        try {
            //await mcpClient.deleteServer(server.id);
        } catch (error) {
            console.error(error);
            pushError(error);
        }
    }

    const openOfferPage = (offer: any) => {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set('tab', 'marketplace');
        nextSearchParams.set('offer', offer._id);
        FILTER_PARAMS.forEach((key) => nextSearchParams.delete(key));
        setSearchParams(nextSearchParams);
    }

    const getRandomIndexes = (count: number, maxLength: number) => {
        const indexes = new Set<number>();
        while (indexes.size < count && indexes.size < maxLength) {
            indexes.add(Math.floor(Math.random() * maxLength));
        }
        return Array.from(indexes);
    }

    const _getRandomMarketplaceOffers = (marketplaceOffers: any[]) => {
        // Temporarily show all offers to debug
        const allOffers = marketplaceOffers;

        if (allOffers.length === 0) {
            return [];
        }

        // If we have 3 or fewer offers, return all of them
        if (allOffers.length <= 3) {
            return allOffers;
        }

        // Otherwise, randomly select 3
        const indexes = getRandomIndexes(3, allOffers.length);
        return allOffers.filter((_, index) => indexes.includes(index));
    }

    const loadMarketplaceOffers = useCallback(async () => {
        try {
            if (!getCachedMarketplaceData()) {
                setLoadingMarketplaceOffers(true);
            }
            const data = await fetchMarketplaceData();

            setMarketplaceOffers(data.offers);
            setTopMarketplaceOffers(data.featured);
        } catch (error) {
            console.error('Error loading marketplace offers:', error);
            pushError(error);
        } finally {
            setLoadingMarketplaceOffers(false);
        }
    }, [pushError]);

    useEffect(() => {
        if (authentication.customer) {
            loadMarketplaceOffers();
        }
    }, [authentication.customer, loadMarketplaceOffers]);

    // Check for openOrder URL parameter and open order dialog when marketplace offers are loaded
    useEffect(() => {
        const openOrderParam = searchParams.get('openOrder');
        const agentToolsetId = searchParams.get('agentToolset');

        if (openOrderParam === 'true' && agentToolsetId && marketplaceOffers.length > 0 && roleAccess.canPlaceOrders) {
            // Find the marketplace offer with the matching agentToolset ID
            const matchingOffer = marketplaceOffers.find((offer: any) => offer._id === agentToolsetId);

            if (matchingOffer) {
                // Remove the openOrder parameter from URL to prevent reopening on re-renders
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.delete('openOrder');
                setSearchParams(newSearchParams, { replace: true });

                // Open the order dialog
                openOrderDialog(matchingOffer);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [marketplaceOffers]);

    // Breadcrumb of the subpages. It lives outside the animated page, so it never fades with it.
    const selectedOffer = offerId ? marketplaceOffers.find((offer: any) => offer._id === offerId) : null;
    const storeCrumb: MarketplaceCrumb = { label: t('agentPanel.store.title'), to: storeHref() };
    const typeText = (type: string) => t(`agentPanel.store.types.${type}.label`, { defaultValue: type });
    let crumbs: MarketplaceCrumb[] | null = null;
    if (tab === 'marketplace' && offerId) {
        crumbs = selectedOffer
            ? [storeCrumb, { label: typeText(selectedOffer._type), to: storeHref({ category: selectedOffer._type }) }, { label: selectedOffer.name }]
            : [storeCrumb];
    } else if (tab === 'marketplace' && hasCollection(searchParams)) {
        const category = parseCategory(searchParams.get('category'));
        const scope = parseScope(searchParams.get('scope'));
        const price = parsePrice(searchParams.get('price'));
        const label = searchParams.get('provider')
            || (category ? typeText(category) : scope !== 'all' ? t(`agentPanel.store.filters.scope.${scope}`) : t(`agentPanel.store.filters.price.${price}`));
        crumbs = [storeCrumb, { label }];
    }

    // A new page of the marketplace always starts at the top, before the transition plays.
    useLayoutEffect(() => {
        scrollToTop(viewTopRef.current);
    }, [viewKey]);

    const orderDisabled = !selectedPlan || !agreedToTerms || hasMultipleBookedPlans
        || (existingProductPlans.length > 0 && !hasUpgradeAvailable) || loadingOrders || loadingBillingInfo || placingOrder;

    return <>
        <div className={CENTERED_PAGE_CLASS}>
            <Tabs value={tab} className="min-w-0">
                {/* No tab strip: the sidebar submenu under „Agentendienste“ is the menu of this area (?tab=). */}

                <TabsContent value="marketplace" className="mt-4 min-w-0">
                    <div ref={viewTopRef} className="scroll-mt-4" />
                    {crumbs && <div className="mb-6"><MarketplaceNav crumbs={crumbs} /></div>}
                    {offerId ? (
                        <MarketplaceOfferPage
                            offer={marketplaceOffers.find((offer: any) => offer._id === offerId) ?? null}
                            loading={loadingMarketplaceOffers}
                            canOrder={roleAccess.canPlaceOrders}
                            owned={marketplaceOffers.some((offer: any) => offer._id === offerId && !isExternalOffer(offer, authentication.customer))}
                            onTest={openTestDialog}
                            onOrder={openOrderDialog}
                        />
                    ) : (
                        <MarketplaceStore
                            offers={marketplaceOffers}
                            featuredOffers={topMarketplaceOffers}
                            loading={loadingMarketplaceOffers}
                            canOrder={(offer) => roleAccess.canPlaceOrders && isExternalOffer(offer, authentication.customer)}
                            isOwned={(offer) => !isExternalOffer(offer, authentication.customer)}
                            onOpenDetails={openOfferPage}
                            onTest={openTestDialog}
                            onOrder={openOrderDialog}
                        />
                    )}
                </TabsContent>

                <TabsContent value="agentToolset" className="mt-4 min-w-0">
                    <AgentToolsetManagement />
                </TabsContent>
                <TabsContent value="documentPacks" className="mt-4 min-w-0">
                    <RAGManagement />
                </TabsContent>
                <TabsContent value="promptTools" className="mt-4 min-w-0">
                    <PromptToolManagement />
                </TabsContent>
                <TabsContent value="agenticWorkflows" className="mt-4 min-w-0">
                    <AgenticWorkflowManagement />
                </TabsContent>
                {hasApiKeyAccess && <TabsContent value="apiKeys" className="mt-4 min-w-0">
                    <ApiKeyManagement />
                </TabsContent>}
            </Tabs>
        </div>

        {/* Dialog: Bestellen / Upgraden */}
        <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
            <DialogContent className="flex max-h-[85vh] max-w-[calc(100vw-2rem)] flex-col overflow-y-auto sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle>
                        {existingProductPlans.length > 0
                            ? t('settingsPanel.agentPanel.upgradeToolset')
                            : t('settingsPanel.agentPanel.orderToolset')}
                    </DialogTitle>
                </DialogHeader>
                {selectedMarketplaceOffer
                    ?
                    <div className="flex min-w-0 flex-col gap-4">
                        <p className="text-sm">
                            {existingProductPlans.length > 0
                                ? (hasMultipleBookedPlans
                                    ? t('agentPanel.multiplePlansBooked')
                                    : (hasUpgradeAvailable
                                        ? t('agentPanel.selectUpgradePlan')
                                        : t('agentPanel.highestPlanBooked')))
                                : t('ui.selectPricingModel')}
                        </p>
                        {(loadingOrders || loadingBillingInfo) ? (
                            <div className="flex flex-col gap-2">
                                <Skeleton className="h-16 w-full" />
                                <Skeleton className="h-16 w-full" />
                                <Skeleton className="h-16 w-full" />
                            </div>
                        ) : (
                            <>
                                {/* Billing Address Form - only show if no LegalPerson exists */}
                                {!hasLegalPerson && (
                                    <div className="flex min-w-0 flex-col gap-3 rounded-md border bg-muted/40 p-4">
                                        <h3 className="text-base font-semibold text-[color:var(--customer-primary-dark,hsl(var(--foreground)))]">
                                            {t('agentPanel.billingAddress')}
                                        </h3>

                                        {renderBillingField({ field: 'company', label: t('agentPanel.billingCompany'), required: true })}

                                        <div className="flex min-w-0 flex-wrap gap-3">
                                            {renderBillingField({
                                                field: 'street',
                                                label: t('agentPanel.billingStreet'),
                                                required: true,
                                                className: 'min-w-[160px] flex-[3]',
                                            })}
                                            {renderBillingField({
                                                field: 'streetNumber',
                                                label: t('agentPanel.billingStreetNumber'),
                                                required: true,
                                                className: 'min-w-[80px] flex-1',
                                            })}
                                        </div>

                                        <div className="flex min-w-0 flex-wrap gap-3">
                                            {renderBillingField({
                                                field: 'postalCode',
                                                label: t('agentPanel.billingPostalCode'),
                                                required: true,
                                                className: 'min-w-[100px] flex-1',
                                            })}
                                            {renderBillingField({
                                                field: 'city',
                                                label: t('agentPanel.billingCity'),
                                                required: true,
                                                className: 'min-w-[160px] flex-[2]',
                                            })}
                                        </div>

                                        <div className="flex min-w-0 flex-col gap-1.5">
                                            <Label htmlFor={billingFieldId('country')}>{t('agentPanel.billingCountry')}</Label>
                                            <Select
                                                value={billingAddress.country}
                                                onValueChange={(value) => setBillingAddress({ ...billingAddress, country: value })}
                                            >
                                                <SelectTrigger id={billingFieldId('country')}>
                                                    <SelectValue placeholder={t('global.pleaseSelect')} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="DE">Deutschland</SelectItem>
                                                    <SelectItem value="AT">Österreich</SelectItem>
                                                    <SelectItem value="CH">Schweiz</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        {/* Optional business details */}
                                        <div className="flex flex-wrap">
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="gap-2 px-0 hover:bg-transparent"
                                                aria-expanded={showBusinessDetails}
                                                onClick={() => setShowBusinessDetails(!showBusinessDetails)}
                                            >
                                                {showBusinessDetails
                                                    ? <ChevronDown aria-hidden className="h-4 w-4" />
                                                    : <ChevronRight aria-hidden className="h-4 w-4" />}
                                                {t('agentPanel.businessDetailsOptional')}
                                            </Button>
                                        </div>

                                        {showBusinessDetails && (
                                            <div className="flex min-w-0 flex-col gap-3">
                                                {renderBillingField({
                                                    field: 'taxId',
                                                    label: t('agentPanel.billingTaxId'),
                                                    placeholder: 'DE123456789',
                                                })}
                                                <div className="flex min-w-0 flex-wrap gap-3">
                                                    {renderBillingField({
                                                        field: 'tradeRegisterCity',
                                                        label: t('agentPanel.billingTradeRegisterCity'),
                                                        placeholder: 'München',
                                                        className: 'min-w-[160px] flex-1',
                                                    })}
                                                    {renderBillingField({
                                                        field: 'tradeRegisterId',
                                                        label: t('agentPanel.billingTradeRegisterId'),
                                                        placeholder: 'HRB 12345',
                                                        className: 'min-w-[160px] flex-1',
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div
                                    role="radiogroup"
                                    aria-label={t('ui.selectPricingModel')}
                                    className="flex h-[400px] min-w-0 flex-col gap-2 overflow-y-auto rounded-md border p-2"
                                >
                                    {(selectedMarketplaceOffer.plans || []).map((plan: any, index: number) => {
                                        const status = getPlanStatus(plan);
                                        const isCurrent = status === 'current';
                                        const isUnavailable = status === 'downgrade' || status === 'incompatible';
                                        const isSelectable = !isCurrent && !isUnavailable;
                                        const isSelected = selectedPlan === plan.name && isSelectable;

                                        return (
                                            <button
                                                key={index}
                                                type="button"
                                                role="radio"
                                                aria-checked={isSelected}
                                                disabled={!isSelectable}
                                                className={cn(
                                                    'flex min-w-0 flex-col gap-1 rounded-md p-4 text-left transition-colors',
                                                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                                                    isCurrent && 'border-l-4 border-l-success bg-success/10',
                                                    isSelected && 'bg-accent',
                                                    isSelectable && !isSelected && 'hover:bg-accent/60',
                                                    isUnavailable && 'cursor-not-allowed opacity-60',
                                                )}
                                                onClick={() => {
                                                    if (isSelectable) {
                                                        setSelectedPlan(plan.name);
                                                    }
                                                }}
                                            >
                                                <span className="flex min-w-0 flex-wrap items-center gap-2 text-base font-semibold">
                                                    {plan.name}
                                                    {getPlanBadge(status)}
                                                </span>
                                                <span className={cn('min-w-0 break-words text-sm', isUnavailable && 'text-muted-foreground')}>
                                                    {plan.description}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                        <div className="flex min-w-0 items-start gap-2">
                            <Checkbox
                                className="mt-0.5"
                                aria-labelledby={termsTextId}
                                checked={agreedToTerms}
                                onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
                                disabled={hasMultipleBookedPlans || (existingProductPlans.length > 0 && !hasUpgradeAvailable)}
                            />
                            {/* Links tragen ihre Erkennbarkeit selbst: durchgehend unterstrichen,
                                nicht nur über Farbe — `--primary` ist in diesem Theme fast schwarz. */}
                            <div id={termsTextId} className="min-w-0 text-sm [&_a]:underline [&_a]:underline-offset-4">
                                <Trans
                                    i18nKey="ui.orderConfirmation"
                                    components={{
                                        termsLink: <a href={selectedMarketplaceOffer.termsAndConditionsURL} target='_blank' rel='noreferrer'>{t('ui.termsAndConditions')}</a>,
                                        dataProtectionLink: <a href={selectedMarketplaceOffer.dataProtectionURL} target='_blank' rel='noreferrer'>{t('ui.dataProtectionPolicy')}</a>,
                                        providerLink: <a href={selectedMarketplaceOffer.provider.website} target='_blank' rel='noreferrer'>{selectedMarketplaceOffer.provider.name}</a>
                                    }}
                                    values={{
                                        termsAndConditions: t('ui.termsAndConditions'),
                                        dataProtectionPolicy: t('ui.dataProtectionPolicy'),
                                        providerName: selectedMarketplaceOffer.provider.name
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    :
                    <></>
                }
                <DialogFooter className="gap-2 pt-4 sm:gap-0">
                    <Button variant="outline" onClick={() => setOrderDialogOpen(false)}>
                        {t('global.cancel')}
                    </Button>
                    <Button
                        disabled={orderDisabled}
                        onClick={async () => {
                            setPlacingOrder(true);
                            try {
                                // Validate billing address if no LegalPerson exists
                                if (!hasLegalPerson) {
                                    if (!validateBillingAddress()) {
                                        return;
                                    }

                                    // Save billing info before creating order
                                    console.log('Updating billing info...');
                                    const billingResult = await userAPI.updateCustomerBillingInfo(authentication.customer, billingAddress);
                                    if (!billingResult.success) {
                                        pushError(new Error(billingResult.error || t('agentPanel.billingUpdateFailed')));
                                        return;
                                    }
                                    console.log('Billing info updated successfully');
                                }

                                console.log('======> selectedPlan', selectedPlan);
                                console.log('======> offer', selectedMarketplaceOffer);

                                const order = {
                                    seller: selectedMarketplaceOffer._tenant,
                                    customer: authentication.customer,
                                    orderDate: new Date(),
                                    expirationDate: dayjs().add(1, 'year').toDate(),
                                    paymentMode: PaymentMode.upFront,
                                    billingPeriod: InvoicingPeriod.monthly,
                                    orderItems: [new OrderItem({
                                        service: selectedMarketplaceOffer._type === 'KnowledgeBase' ? ServiceType.knowledgeBaseUsage
                                            : selectedMarketplaceOffer._type === 'AgentToolset' ? ServiceType.agentToolsetUsage
                                            : selectedMarketplaceOffer._type === 'PromptTool' ? ServiceType.promptToolUsage
                                            : selectedMarketplaceOffer._type === 'AgenticWorkflow' ? ServiceType.agenticWorkflowUsage
                                            : ServiceType.agentToolsetUsage,
                                        serviceReference: selectedMarketplaceOffer._id,
                                        plan: selectedMarketplaceOffer.plans.find((plan: any) => plan.name === selectedPlan)
                                    })]
                                };

                                console.log('======> order', order);

                                await userAPI.createServiceOrder(authentication.customer, order);

                                setOrderDialogOpen(false);
                            } catch (error) {
                                pushError(error);
                            } finally {
                                setPlacingOrder(false);
                            }
                        }}
                    >
                        {placingOrder && <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />}
                        {existingProductPlans.length > 0 ? t('settingsPanel.agentPanel.upgrade') : t('settingsPanel.agentPanel.order')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>

        {TestDialog}
        <PublishDialog />
    </>;
}
